import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search as SearchIcon, Book, User, Users, AlertCircle, RotateCcw, Sparkles, Hash, X, Camera, Loader2, Globe, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { useRecommendedUsers } from '@/hooks/useRecommendedUsers';
import { useSearchTracking } from '@/hooks/useSearchTracking';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DinoAvatar } from '@/components/DinoAvatar';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { BookCard } from '@/components/BookCard';
import { CoverImage } from '@/components/CoverImage';
import { ReviewTBRButtons } from '@/components/ReviewTBRButtons';
import { BarcodeScanner } from '@/components/BarcodeScanner';
import { EmptyState } from '@/components/EmptyState';
import { useToast } from '@/hooks/use-toast';
import { normalizeOpenLibraryId } from '@/lib/searchUtils';
import { BookResult, UserResult } from '@/types/search';
import { SEARCH_LANGUAGES } from '@/lib/constants';
import { useRecentSearches } from '@/hooks/useRecentSearches';
import { isISBNQuery, validateISBN } from '@/utils/isbnUtils';
import { sortBySearchQuality } from '@/lib/searchResultSorting';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
} from "@/components/ui/pagination";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

// Separate title and author state - no longer using combined query parsing

export default function Search() {
  // Separate title and author inputs for structured search
  const [titleQuery, setTitleQuery] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('title') || urlParams.get('q') || '';
  });
  const [authorQuery, setAuthorQuery] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('author') || '';
  });
  // Combined query for backwards compatibility with recent searches
  const query = titleQuery;
  const [bookResults, setBookResults] = useState<BookResult[]>([]);
  const [lowConfidenceResults, setLowConfidenceResults] = useState<BookResult[]>([]);
  const [showLowConfidence, setShowLowConfidence] = useState(false);
  const [externalBookResults, setExternalBookResults] = useState<BookResult[]>([]);
  const [userResults, setUserResults] = useState<UserResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingExternal, setLoadingExternal] = useState(false);
  const [localCount, setLocalCount] = useState(0);
  const [externalCount, setExternalCount] = useState(0);
  const [searchSource, setSearchSource] = useState<'local' | 'external' | 'both'>('local');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchType, setSearchType] = useState<'title' | 'author' | 'isbn'>('title');
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('lang') || 'eng';
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [activeTab, setActiveTab] = useState<'books' | 'people' | 'suggested'>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    if (tab === 'people' || tab === 'suggested') return tab;
    return 'books';
  });
  const [searchError, setSearchError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<BookResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsType, setSuggestionsType] = useState<'personalized' | 'popular'>('popular');
  const [showSearchTips, setShowSearchTips] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: recommendations, isLoading: recommendationsLoading } = useRecommendedUsers();
  const [followingLoading, setFollowingLoading] = useState<Record<string, boolean>>({});
  const { recentSearches, addRecentSearch, clearRecentSearches } = useRecentSearches();
  const { trackImpression, trackClick, trackTBRAdd, trackTrendingSearch } = useSearchTracking();
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  
  const isDebug = new URLSearchParams(window.location.search).has('debug');

  // Track latest request IDs to drop stale responses
  const bookReqIdRef = useRef(0);
  const userReqIdRef = useRef(0);

  // Memoize book results with stable keys and quality sorting to prevent unnecessary re-renders
  const memoizedBookResults = useMemo(() => {
    // Sort by quality score to prioritize better results
    const sortedResults = sortBySearchQuality(bookResults);
    return sortedResults.map((book, index) => ({
      ...book,
      stableKey: book.id || book.open_library_id || `search-${index}-${book.title}`,
    }));
  }, [bookResults]);


  const searchBooks = useCallback(async (title: string, author: string, page: number, lang: string, type: 'title' | 'author' | 'isbn', source: 'local' | 'external' = 'local') => {
    const requestId = ++bookReqIdRef.current;
    
    if (isDebug) console.log(`[BOOK SEARCH] Starting search:`, { title, author, page, lang, type, source, requestId });
    
    // Reset low-confidence UI on new searches, but keep main results visible
    // until new results arrive to prevent flashing empty state between keystrokes
    if (source === 'local' && page === 1) {
      setShowLowConfidence(false);
    }
    
    if (source === 'external') {
      setLoadingExternal(true);
    } else {
      setLoading(true);
    }
    setSearchError(null);

    try {
      const { data, error } = await supabase.functions.invoke('search-books', {
        body: { 
          query: title.trim(),
          authorQuery: author.trim() || null, // Separate author parameter for structured search
          type, 
          page, 
          limit: 20, 
          language: lang, 
          source,
          sessionId: sessionIdRef.current,
          debug: isDebug
        },
      });

      if (error) {
        throw error;
      }

      if (isDebug) console.log(`[BOOK SEARCH] Received results:`, { requestId, currentReqId: bookReqIdRef.current, resultsCount: data?.results?.length, source });

      if (requestId === bookReqIdRef.current) {
        if (source === 'external') {
          if (isDebug) console.log(`[BOOK SEARCH] Setting external results:`, data.results?.length);
          setExternalBookResults(data.results || []);
          setExternalCount(data.externalCount || 0);
          
          // Track impressions for external results
          (data.results || []).forEach((book: BookResult, index: number) => {
            if (book.id || book.open_library_id) {
              trackImpression({
                query: title,
                searchType: type,
                bookId: book.id || book.open_library_id!,
                position: index,
                source: 'isbndb',
              });
            }
          });
        } else {
          if (isDebug) console.log(`[BOOK SEARCH] Setting local results:`, data.results?.length, 'low confidence:', data.lowConfidenceResults?.length);
          setBookResults(data.results || []);
          setLowConfidenceResults(data.lowConfidenceResults || []);
          setLocalCount(data.total || data.localCount || 0);
          setExternalCount(data.externalCount || 0);
          setHasMoreResults(data.hasMore || false);
          
          // Track impressions for local results
          (data.results || []).forEach((book: BookResult, index: number) => {
            if (book.id) {
              trackImpression({
                query: title,
                searchType: type,
                bookId: book.id,
                position: index,
                source: book.isLocal ? 'local' : 'isbndb',
              });
            }
          });
          
          // Track trending search
          trackTrendingSearch(title);
          
          // Note: Auto-fallback to external sources is now handled by the edge function
          // The autoFallback flag in the response indicates if external sources were used
        }
        addRecentSearch(title, type);
      } else {
        if (isDebug) console.log(`[BOOK SEARCH] Dropping stale response:`, { requestId, currentReqId: bookReqIdRef.current });
      }
    } catch (error) {
      console.error('[BOOK SEARCH] Error:', error);
      if (requestId === bookReqIdRef.current) {
        setSearchError(error instanceof Error ? error.message : 'Search failed');
        if (source === 'external') {
          setExternalBookResults([]);
        } else {
          setBookResults([]);
        }
      }
    } finally {
      // Only clear loading for the current request to prevent flashing
      if (requestId === bookReqIdRef.current) {
        if (source === 'external') {
          setLoadingExternal(false);
        } else {
          setLoading(false);
        }
      }
    }
  }, [addRecentSearch, isDebug]);

  const searchUsers = useCallback(async (searchQuery: string) => {
    const q = searchQuery.trim();
    if (!q) {
      setUserResults([]);
      setLoadingUsers(false);
      return;
    }

    // Increment request id to cancel stale responses
    userReqIdRef.current += 1;
    const requestId = userReqIdRef.current;

    setLoadingUsers(true);
    try {
      const { data: users } = await supabase
        .rpc('search_public_profiles', { 
          search_term: q, 
          excluding_user_id: user?.id 
        })
        .limit(20);

      // Drop stale response
      if (requestId !== userReqIdRef.current) return;

      if (users && users.length > 0) {
        // Batch check follow status for displayed users
        const { data: follows } = await supabase
          .from('follows')
          .select('following_id')
          .eq('follower_id', user?.id)
          .in('following_id', users.map(u => u.id));

        const followingIds = new Set(follows?.map(f => f.following_id) || []);
        const usersWithFollowStatus = users.map(u => ({
          ...u,
          isFollowing: followingIds.has(u.id),
        }));

        setUserResults(usersWithFollowStatus);
      } else {
        setUserResults([]);
      }
    } catch (error) {
      // Only report if this is the latest request
      if (requestId === userReqIdRef.current) {
        console.error('User search error:', error);
        toast({
          variant: "destructive",
          title: "Search failed",
          description: "There was an error searching for users.",
        });
      }
    } finally {
      if (requestId === userReqIdRef.current) {
        setLoadingUsers(false);
      }
    }
  }, [user?.id, toast]);

  // Sync URL with query, tab, and language
  useEffect(() => {
    const url = new URL(window.location.href);
    if (query) {
      url.searchParams.set('q', query);
    } else {
      url.searchParams.delete('q');
    }
    if (activeTab !== 'books') {
      url.searchParams.set('tab', activeTab);
    } else {
      url.searchParams.delete('tab');
    }
    if (selectedLanguage !== 'eng') {
      url.searchParams.set('lang', selectedLanguage);
    } else {
      url.searchParams.delete('lang');
    }
    const newUrl = url.toString();
    if (newUrl !== window.location.href) {
      window.history.replaceState({}, '', newUrl);
    }
  }, [query, activeTab, selectedLanguage]);

  // Debounced search - 350ms consistent with useBookSearch hook
  useEffect(() => {
    // Clear results immediately when query is empty
    if (activeTab === 'books' && !query.trim() && !authorQuery.trim()) {
      setBookResults([]);
      setExternalBookResults([]);
      return;
    }

    const timer = setTimeout(() => {
      if (activeTab === 'books') {
        if (titleQuery.trim() || authorQuery.trim()) {
          setCurrentPage(1);
          searchBooks(titleQuery, authorQuery, 1, selectedLanguage, searchType);
        }
      } else {
        // For people search, require at least 2 characters
        if (!titleQuery || titleQuery.trim().length < 2) {
          setUserResults([]);
          setLoadingUsers(false);
          return;
        }
        searchUsers(titleQuery);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [titleQuery, authorQuery, activeTab, selectedLanguage, searchType]); // Removed searchBooks and searchUsers from deps

  const goToPage = (page: number) => {
    console.log(`[Pagination] Going to page: ${page}`);
    setCurrentPage(page);
    searchBooks(titleQuery, authorQuery, page, selectedLanguage, searchType);
    // Scroll to top of results
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchTypeChange = (newType: 'title' | 'author' | 'isbn') => {
    setSearchType(newType);
    setCurrentPage(1);
    setSearchSource('local');
    // Clear both local and external results to prevent stale data
    setBookResults([]);
    setExternalBookResults([]);
  };

  const handleSearchExternal = () => {
    setSearchSource('external');
    searchBooks(titleQuery, authorQuery, 1, selectedLanguage, searchType, 'external');
  };

  const loadSuggestions = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke('search-suggestions');
      if (error) throw error;
      
      setSuggestions(data.suggestions || []);
      setSuggestionsType(data.type || 'popular');
    } catch (error) {
      console.error('Error loading suggestions:', error);
    }
  }, []);

  // Load suggestions on mount
  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  const handleBookClick = async (book: BookResult) => {
    try {
      // Track click event - use ISBN as fallback identifier
      if (book.id || book.open_library_id || book.isbn13) {
        trackClick(book.id || book.open_library_id || book.isbn13!);
      }

      // If we have a valid UUID, navigate directly (covers local books and already-resolved books)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (book.id && uuidRegex.test(book.id)) {
        navigate(`/book/${book.id}`);
        return;
      }

      const normalizedId = normalizeOpenLibraryId(book.open_library_id || '');
      const hasISBN = book.isbn13 || book.isbn10;
      
      // Must have either OLID or ISBN
      if (!normalizedId && !hasISBN) {
        throw new Error('Invalid book identifier');
      }
      
      // Build dynamic query based on available identifiers
      const queryParts: string[] = [];
      if (normalizedId) {
        queryParts.push(`open_library_id.eq.${normalizedId}`);
        queryParts.push(`open_library_id.eq./works/${normalizedId}`);
      }
      if (book.isbn13) {
        queryParts.push(`isbn13.eq.${book.isbn13}`);
        queryParts.push(`canonical_isbn13.eq.${book.isbn13}`);
      }
      if (book.isbn10) {
        queryParts.push(`isbn10.eq.${book.isbn10}`);
      }
      
      // Check for existing book with multi-factor validation
      const { data: existingBooks } = await supabase
        .from('books')
        .select('id, published_year, isbn13, isbn10, open_library_id, canonical_isbn13')
        .or(queryParts.join(','))
        .limit(5);

      // Validate it's actually the same book using multiple factors
      const matchingBook = existingBooks?.find(existing => {
        const yearMatch = !book.published_year || !existing.published_year || 
                          existing.published_year === book.published_year;
        const isbnMatch = (book.isbn13 && book.isbn13 === existing.isbn13) || 
                         (book.isbn10 && book.isbn10 === existing.isbn10) ||
                         (book.isbn13 && book.isbn13 === existing.canonical_isbn13);
        const olidMatch = normalizedId && (existing.open_library_id === normalizedId || 
                         existing.open_library_id === `/works/${normalizedId}`);
        
        // Must match on OLID + year, OR have matching ISBN
        return (olidMatch && yearMatch) || isbnMatch;
      });

      if (matchingBook) {
        navigate(`/book/${matchingBook.id}`);
        return;
      }

      // Create new book - OLID is optional, ISBN can be primary identifier
      // Set identity_locked based on source (ISBNdb = locked, others = unlocked)
      const bookSource = normalizedId ? 'openlibrary' : 'isbndb';
      
      // Generate work group key for edition collapsing
      const normalizedTitle = (book.title || '').toLowerCase().split(/[:\-–—(]/)[0].replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
      const normalizedAuthor = ((book.authors || [])[0] || '').toLowerCase().replace(/[.,']/g, '').replace(/\s+/g, ' ').trim();
      const workGroupKey = `${normalizedTitle}::${normalizedAuthor}`;
      
      // Calculate source confidence score based on source quality
      const sourceConfidence = bookSource === 'isbndb' ? 95 : 50;
      
      const bookData = {
        ...(normalizedId && { open_library_id: normalizedId }),
        title: book.title || 'Untitled',
        subtitle: book.subtitle,
        authors: book.authors || [],
        published_year: book.published_year,
        cover_url: book.cover_url,
        isbn10: book.isbn10,
        isbn13: book.isbn13,
        canonical_isbn13: book.isbn13,
        synopsis: book.synopsis,
        source: bookSource,
        language: selectedLanguage === 'eng' ? 'en' : selectedLanguage,
        // NEW: Provenance fields
        primary_source: bookSource,
        sources_seen: [bookSource],
        source_confidence: sourceConfidence,
        work_group_key: workGroupKey,
        // ISBNdb-sourced books get identity locked immediately
        identity_locked: bookSource === 'isbndb',
        identity_lock_reason: bookSource === 'isbndb' ? 'Created from canonical ISBNdb search result' : null,
        identity_locked_at: bookSource === 'isbndb' ? new Date().toISOString() : null,
        // Only pending enrichment if not from ISBNdb
        enrichment_status: bookSource === 'isbndb' ? 'completed' : 'pending',
        data_sources: {
          [bookSource]: true,
          sources_used: [bookSource],
        },
      };

      const { data: newBook, error } = await supabase
        .from('books')
        .insert(bookData)
        .select('id')
        .single();

      if (error) {
        // Handle duplicate ISBN constraint violation
        if (error.code === '23505' && book.isbn13) {
          const { data: existingByIsbn } = await supabase
            .from('books')
            .select('id')
            .or(`isbn13.eq.${book.isbn13},canonical_isbn13.eq.${book.isbn13}`)
            .limit(1)
            .single();
          
          if (existingByIsbn?.id) {
            toast({
              title: "Found existing edition",
              description: "This book already exists in our database. Showing the existing entry.",
            });
            navigate(`/book/${existingByIsbn.id}`);
            return;
          }
        }
        throw error;
      }
      
      if (!newBook?.id) throw new Error('Failed to create book');

      navigate(`/book/${newBook.id}`);
    } catch (error) {
      console.error('Error handling book click:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "There was an error opening the book details.",
      });
    }
  };

  const retrySearch = () => {
    if (titleQuery) {
      setCurrentPage(1);
      searchBooks(titleQuery, authorQuery, 1, selectedLanguage, searchType);
    }
  };

  const handleFollowToggle = async (targetUser: any) => {
    if (!user) return;

    setFollowingLoading(prev => ({ ...prev, [targetUser.id]: true }));

    try {
      if (targetUser.isFollowing) {
        // Unfollow
        await supabase
          .from('follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', targetUser.id);

        toast({
          title: "Unfollowed",
          description: `You unfollowed ${targetUser.name || targetUser.username}.`,
        });

        // Update local state for recommendedUsers cache
        queryClient.setQueryData(['recommendedUsers'], (old: any) => {
          if (!old) return old;
          const updateUser = (u: any) => u.id === targetUser.id ? { ...u, isFollowing: false } : u;
          return {
            mutualFollows: old.mutualFollows?.map(updateUser) || [],
            similarReaders: old.similarReaders?.map(updateUser) || [],
            trendingUsers: old.trendingUsers?.map(updateUser) || []
          };
        });
        
        // Also update userResults state for People tab
        setUserResults(prev => prev.map(u => 
          u.id === targetUser.id ? { ...u, isFollowing: false } : u
        ));
      } else {
        // Follow
        await supabase
          .from('follows')
          .insert({
            follower_id: user.id,
            following_id: targetUser.id,
          });

        toast({
          title: "Following",
          description: `You are now following ${targetUser.name || targetUser.username}.`,
        });

        // Update local state for recommendedUsers cache
        queryClient.setQueryData(['recommendedUsers'], (old: any) => {
          if (!old) return old;
          const updateUser = (u: any) => u.id === targetUser.id ? { ...u, isFollowing: true } : u;
          return {
            mutualFollows: old.mutualFollows?.map(updateUser) || [],
            similarReaders: old.similarReaders?.map(updateUser) || [],
            trendingUsers: old.trendingUsers?.map(updateUser) || []
          };
        });
        
        // Also update userResults state for People tab
        setUserResults(prev => prev.map(u => 
          u.id === targetUser.id ? { ...u, isFollowing: true } : u
        ));
      }
    } catch (error) {
      console.error('Follow toggle error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was an error updating follow status.",
      });
    } finally {
      setFollowingLoading(prev => ({ ...prev, [targetUser.id]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8 w-full max-w-full overflow-x-hidden">
      {/* Sticky Search Header - More compact on mobile */}
      <div className="sticky top-[var(--header-height)] z-40 bg-background border-b border-border py-2 sm:py-4">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="max-w-2xl mx-auto space-y-2 sm:space-y-3">
            {/* Search Inputs Container with Border */}
            <div className="border border-border rounded-lg p-3 sm:p-4 bg-card space-y-3">
              {/* Title Input */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Book className="absolute left-3 top-2.5 sm:top-3.5 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                  <Input
                    placeholder={searchType === 'isbn' ? "Enter ISBN..." : "Book title"}
                    value={titleQuery}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTitleQuery(value);
                      if (searchType === 'isbn' && value.length >= 10) {
                        const { valid } = validateISBN(value);
                        if (!valid && value.length >= 13) {
                          console.log('Invalid ISBN format');
                        }
                      }
                    }}
                    onFocus={() => searchType !== 'isbn' && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className="pl-9 sm:pl-10 pr-10 h-10 sm:h-12 text-sm sm:text-base"
                    autoFocus
                  />
                  {titleQuery && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setTitleQuery('');
                        setBookResults([]);
                      }}
                      className="absolute right-1 top-1 h-8 w-8 sm:h-10 sm:w-10"
                    >
                      <X className="h-4 w-4 sm:h-5 sm:w-5" />
                    </Button>
                  )}
                </div>
                
                {/* Language Selector - Compact */}
                {activeTab === 'books' && (
                  <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                    <SelectTrigger className="w-auto h-10 sm:h-12 px-2 sm:px-3 shrink-0">
                      <Globe className="w-4 h-4" />
                      <span className="hidden sm:inline ml-1">{SEARCH_LANGUAGES.find(l => l.code === selectedLanguage)?.flag}</span>
                    </SelectTrigger>
                    <SelectContent>
                      {SEARCH_LANGUAGES.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          {lang.flag} {lang.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Author Input - Only show when not in ISBN mode */}
              {searchType !== 'isbn' && (
                <div className="relative">
                  <User className="absolute left-3 top-2.5 sm:top-3.5 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                  <Input
                    placeholder="Author"
                    value={authorQuery}
                    onChange={(e) => setAuthorQuery(e.target.value)}
                    className="pl-9 sm:pl-10 pr-10 h-10 sm:h-12 text-sm sm:text-base"
                  />
                  {authorQuery && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setAuthorQuery('')}
                      className="absolute right-1 top-1 h-8 w-8 sm:h-10 sm:w-10"
                    >
                      <X className="h-4 w-4 sm:h-5 sm:w-5" />
                    </Button>
                  )}
                </div>
            )}
            </div>

            {/* Search Type Toggle + Scan Button - Compact on mobile */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              <ToggleGroup 
                type="single" 
                value={searchType} 
                onValueChange={(value) => value && handleSearchTypeChange(value as 'title' | 'author' | 'isbn')}
                className="flex-1 rounded-full border border-border bg-card p-0.5 sm:p-1"
              >
                <ToggleGroupItem 
                  value="title" 
                  className="flex-1 h-8 sm:h-9 gap-1 rounded-full text-xs sm:text-sm text-foreground data-[state=on]:bg-secondary data-[state=on]:text-secondary-foreground data-[state=on]:shadow-sm" 
                  aria-label="Search by title"
                >
                  <Book className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="hidden xs:inline sm:inline">Title</span>
                </ToggleGroupItem>
                <ToggleGroupItem 
                  value="isbn" 
                  className="flex-1 h-8 sm:h-9 gap-1 rounded-full text-xs sm:text-sm text-foreground data-[state=on]:bg-secondary data-[state=on]:text-secondary-foreground data-[state=on]:shadow-sm" 
                  aria-label="Search by ISBN"
                >
                  <Hash className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="hidden xs:inline sm:inline">ISBN</span>
                </ToggleGroupItem>
              </ToggleGroup>

              {/* Compact Scan Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowBarcodeScanner(true)}
                className="h-8 w-8 sm:h-9 sm:w-9 shrink-0"
                title="Scan barcode"
              >
                <Camera className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Barcode Scanner Dialog */}
            <BarcodeScanner
              open={showBarcodeScanner}
              onClose={() => setShowBarcodeScanner(false)}
              onScan={(isbn) => {
                setTitleQuery(isbn);
                setSearchType('isbn');
                toast({
                  title: "ISBN Scanned",
                  description: "Searching for book...",
                });
              }}
            />
              
            {/* Suggestions dropdown - hidden when query exists */}
            {showSuggestions && !titleQuery && suggestions.length > 0 && activeTab === 'books' && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-card border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                <div className="p-3 border-b">
                  <p className="text-sm font-medium">
                    {suggestionsType === 'personalized' 
                      ? 'Books by authors you\'ve read' 
                      : 'Popular books'}
                  </p>
                </div>
                <div className="p-2">
                  {suggestions.map((book) => (
                    <button
                      key={book.id || book.open_library_id}
                      onClick={() => {
                        setTitleQuery(book.title);
                        setAuthorQuery(book.authors?.[0] || '');
                        setShowSuggestions(false);
                      }}
                      className="w-full flex items-center gap-3 p-2 hover:bg-accent rounded-lg transition-colors text-left"
                    >
                      <CoverImage
                        bookId={book.id}
                        isbn13={book.isbn13}
                        isbn10={book.isbn10}
                        olid={normalizeOpenLibraryId(book.open_library_id || '')}
                        title={book.title}
                        author={book.authors?.[0]}
                        size="xs"
                        className="w-10 h-14 rounded shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{book.title}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {book.authors?.join(', ')}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Tabs - more compact on mobile */}
        <div className="max-w-4xl mx-auto">
          <Tabs value={activeTab} onValueChange={(value) => {
            setActiveTab(value as 'books' | 'people' | 'suggested');
            // Update URL to reflect tab change
            const url = new URL(window.location.href);
            url.searchParams.set('tab', value);
            window.history.replaceState({}, '', url.toString());
          }}>
            <TabsList className="grid w-full grid-cols-3 max-w-sm sm:max-w-md mx-auto h-9 sm:h-10">
              <TabsTrigger value="books" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <Book className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Books</span>
              </TabsTrigger>
              <TabsTrigger value="people" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>People</span>
              </TabsTrigger>
              <TabsTrigger value="suggested" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Suggested</span>
              </TabsTrigger>
            </TabsList>

            {/* Inline Recent Searches - Subtle chips below tabs */}
            {recentSearches.length > 0 && !titleQuery && activeTab === 'books' && (
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap mt-3 sm:mt-4 text-sm">
                <span className="text-muted-foreground text-xs">Recent:</span>
                {recentSearches.slice(0, 3).map((search, idx) => (
                  <button
                    key={idx}
                    className="px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-muted hover:bg-accent text-xs sm:text-sm transition-colors"
                    onClick={() => {
                      setTitleQuery(search.query);
                      setSearchType(search.type);
                    }}
                  >
                    {search.query}
                  </button>
                ))}
                <button
                  onClick={clearRecentSearches}
                  className="text-muted-foreground hover:text-foreground text-xs"
                >
                  Clear
                </button>
              </div>
            )}

            {/* Books Tab */}
            <TabsContent value="books" className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
              {searchError ? (
                <Card className="card-elevated">
                  <CardContent className="p-6 sm:p-8 text-center space-y-4">
                    <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-destructive mx-auto" />
                    <h3 className="font-semibold text-sm sm:text-base">{searchError}</h3>
                    <Button onClick={retrySearch} variant="outline" size="sm">
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Try again
                    </Button>
                  </CardContent>
                </Card>
              ) : (loading && bookResults.length === 0) ? (
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Searching...</span>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="animate-pulse">
                        <div className="aspect-[2/3] bg-muted/20 rounded-md" />
                        <div className="mt-2 space-y-1">
                          <div className="h-3 bg-muted/40 rounded" />
                          <div className="h-2 bg-muted/30 rounded w-3/4" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
               ) : (bookResults.length > 0 || externalBookResults.length > 0) ? (
                <>
                  {/* Local Results Section */}
                  {bookResults.length > 0 && (
                    <div className="space-y-3 sm:space-y-4">
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                        <Book className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        <span>{localCount} book{localCount !== 1 ? 's' : ''} found</span>
                      </div>
                      {/* Book Results Grid - 3 cols on mobile for compact view */}
                      <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4">
                        {memoizedBookResults.map((book) => (
                          <div key={book.stableKey} className="flex flex-col">
                            <BookCard
                              book={{
                                id: book.id || book.open_library_id || '',
                                title: book.title,
                                subtitle: book.subtitle,
                                authors: book.authors,
                                publishedYear: book.published_year,
                                isbn13: book.isbn13,
                                isbn10: book.isbn10,
                                olid: normalizeOpenLibraryId(book.open_library_id || ''),
                                coverUrl: book.isLocal ? book.cover_url : undefined,
                              }}
                              onClick={() => handleBookClick(book)}
                              showShare={false}
                              variant="search"
                            />
                            <ReviewTBRButtons 
                              book={book} 
                              onTBRAdd={(bookId) => trackTBRAdd(bookId)}
                              onRatingClick={(bookId) => trackClick(bookId)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Low Confidence Results Section - Only show if high-quality results < 3 */}
                  {lowConfidenceResults.length > 0 && bookResults.length < 3 && (
                    <div className="border-t pt-4 sm:pt-6 mt-4 sm:mt-6">
                      <Button
                        variant="ghost"
                        onClick={() => setShowLowConfidence(!showLowConfidence)}
                        className="w-full text-muted-foreground hover:text-foreground text-xs sm:text-sm"
                        size="sm"
                      >
                        {showLowConfidence ? (
                          <>
                            <ChevronUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                            Hide {lowConfidenceResults.length} more
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                            Show {lowConfidenceResults.length} more
                          </>
                        )}
                      </Button>
                      
                      {showLowConfidence && (
                        <div className="mt-3 sm:mt-4 space-y-3 sm:space-y-4">
                          <Alert variant="warning" className="text-xs sm:text-sm">
                            <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                            <AlertDescription className="text-xs sm:text-sm">
                              These results may have incomplete metadata.
                            </AlertDescription>
                          </Alert>
                          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 opacity-80">
                            {lowConfidenceResults.map((book, index) => (
                              <div key={book.id || book.open_library_id || `low-${index}`} className="flex flex-col">
                                <BookCard
                                  book={{
                                    id: book.id || book.open_library_id || '',
                                    title: book.title,
                                    subtitle: book.subtitle,
                                    authors: book.authors,
                                    publishedYear: book.published_year,
                                    isbn13: book.isbn13,
                                    isbn10: book.isbn10,
                                    olid: normalizeOpenLibraryId(book.open_library_id || ''),
                                    coverUrl: book.isLocal ? book.cover_url : undefined,
                                  }}
                                  onClick={() => handleBookClick(book)}
                                  showShare={false}
                                  variant="search"
                                />
                                <ReviewTBRButtons 
                                  book={book}
                                  onTBRAdd={(bookId) => trackTBRAdd(bookId)}
                                  onRatingClick={(bookId) => trackClick(bookId)}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Search External Button */}
                  {bookResults.length > 0 && !searchSource.includes('external') && localCount < 10 && (
                    <div className="flex justify-center pt-6 pb-2 border-t border-border/30 mt-6">
                      <Button
                        onClick={handleSearchExternal}
                        variant="outline"
                        size="default"
                        className="gap-2"
                        disabled={loadingExternal}
                      >
                        {loadingExternal ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Searching...
                          </>
                        ) : (
                          <>
                            <Globe className="h-4 w-4" />
                            Search external sources
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* External Results Section */}
                  {externalBookResults.length > 0 && (
                    <div className="space-y-3 sm:space-y-4 mt-6 sm:mt-8 pt-4 sm:pt-8 border-t">
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                        <SearchIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        <span>{externalCount} result{externalCount !== 1 ? 's' : ''} from external sources</span>
                      </div>
                      <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4">
                        {externalBookResults.map((book, index) => (
                          <div key={book.open_library_id || index} className="flex flex-col">
                            <BookCard
                              book={{
                                id: book.id || book.open_library_id || '',
                                title: book.title,
                                subtitle: book.subtitle,
                                authors: book.authors,
                                publishedYear: book.published_year,
                                isbn13: book.isbn13,
                                isbn10: book.isbn10,
                                olid: normalizeOpenLibraryId(book.open_library_id || ''),
                                coverUrl: undefined,
                              }}
                              onClick={() => handleBookClick(book)}
                              showShare={false}
                              variant="search"
                            />
                            <ReviewTBRButtons 
                              book={book}
                              onTBRAdd={(bookId) => trackTBRAdd(bookId)}
                              onRatingClick={(bookId) => trackClick(bookId)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {isDebug && (
                    <div className="text-xs text-muted-foreground text-center py-2">
                      Page {currentPage}, Local: {localCount}, External: {externalCount}, Low confidence: {lowConfidenceResults.length}
                    </div>
                  )}
                </>
              ) : query && !loading && bookResults.length === 0 ? (
                <div className="space-y-6">
                  <EmptyState
                    icon={Book}
                    title="No books found"
                    description="We couldn't find that in our library. Try the tips below or search external sources."
                    action={
                      (!searchSource.includes('external') || externalBookResults.length === 0) ? (
                        <Button
                          onClick={handleSearchExternal}
                          variant="default"
                          size="lg"
                          className="gap-2"
                          disabled={loadingExternal}
                        >
                          {loadingExternal ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Searching...
                            </>
                          ) : (
                            <>
                              <Globe className="h-4 w-4" />
                              Search for more books
                            </>
                          )}
                        </Button>
                      ) : undefined
                    }
                  />
                  
                  {/* Search Tips Card */}
                  <Card className="bg-muted/30 border-dashed">
                    <CardContent className="p-4 sm:p-6">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        Search Tips
                      </h4>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex items-start gap-2">
                          <span className="text-primary">•</span>
                          <span>Try just the main title without subtitles (e.g., "Atomic Habits" instead of "Atomic Habits: An Easy & Proven Way...")</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-primary">•</span>
                          <span>For author searches, try "Last Name, First Name" or just the last name</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-primary">•</span>
                          <span>Check spelling, especially for unusual names or transliterated titles</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-primary">•</span>
                          <span>Use the barcode scanner to search by ISBN for exact matches</span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <EmptyState
                  icon={SearchIcon}
                  title="Find your next read"
                  description="Search by book title, author name, or ISBN to discover new books."
                />
              )}

               {/* Pagination - compact on mobile */}
               {bookResults.length > 0 && !loading && (
                 <div className="flex justify-center pt-4 sm:pt-6 pb-4">
                   <Pagination>
                     <PaginationContent className="gap-0.5 sm:gap-1">
                       <PaginationItem>
                         <PaginationPrevious 
                           onClick={(e) => {
                             e.preventDefault();
                             if (currentPage > 1) goToPage(currentPage - 1);
                           }}
                           className={`${currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'} h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm`}
                         />
                       </PaginationItem>
                       
                       {/* Show page numbers - fewer on mobile */}
                       {[...Array(Math.max(1, hasMoreResults ? currentPage + 2 : currentPage))].map((_, idx) => {
                         const pageNum = idx + 1;
                         const shouldShow = 
                           pageNum === 1 || 
                           pageNum === currentPage ||
                           Math.abs(pageNum - currentPage) === 1 ||
                           (hasMoreResults && pageNum === currentPage + 2);
                         
                         if (!shouldShow) {
                           if (pageNum === 2 && currentPage > 3) {
                             return (
                               <PaginationItem key={`ellipsis-${pageNum}`}>
                                 <span className="flex h-8 sm:h-9 w-6 sm:w-9 items-center justify-center text-muted-foreground text-xs sm:text-sm">...</span>
                               </PaginationItem>
                             );
                           }
                           return null;
                         }
                         
                         return (
                           <PaginationItem key={pageNum}>
                             <PaginationLink
                               onClick={(e) => {
                                 e.preventDefault();
                                 goToPage(pageNum);
                               }}
                               isActive={pageNum === currentPage}
                               className="cursor-pointer h-8 sm:h-9 w-8 sm:w-9 text-xs sm:text-sm"
                             >
                               {pageNum}
                             </PaginationLink>
                           </PaginationItem>
                         );
                       })}
                       
                       <PaginationItem>
                         <PaginationNext 
                           onClick={(e) => {
                             e.preventDefault();
                             if (hasMoreResults) goToPage(currentPage + 1);
                           }}
                           className={`${!hasMoreResults ? 'pointer-events-none opacity-50' : 'cursor-pointer'} h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm`}
                         />
                       </PaginationItem>
                     </PaginationContent>
                   </Pagination>
                 </div>
               )}
            </TabsContent>

            {/* People Tab */}
            <TabsContent value="people" className="space-y-4">
              {loadingUsers && userResults.length === 0 ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Card key={i} className="animate-pulse">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 bg-muted/40 rounded-full" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-muted/40 rounded w-1/4" />
                          <div className="h-3 bg-muted/30 rounded w-1/3" />
                        </div>
                        <div className="h-8 w-16 bg-muted/40 rounded" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : userResults.length > 0 ? (
                <div className="space-y-4">
                  {userResults.map((person) => (
                    <Card key={person.id} className="card-elevated">
                      <CardContent className="p-4 flex items-center gap-4">
                        <DinoAvatar 
                          src={person.image}
                          name={person.name || person.username}
                          size="lg"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold truncate">
                            {person.name || person.username}
                          </h3>
                          {person.username && person.name && (
                            <p className="text-sm text-muted-foreground truncate">
                              @{person.username}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={person.isFollowing ? "outline" : "default"}
                            disabled={followingLoading[person.id]}
                            onClick={() => handleFollowToggle(person)}
                          >
                            {followingLoading[person.id] ? 'Loading...' : person.isFollowing ? 'Following' : 'Follow'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/u/${person.username || person.id}`)}
                          >
                            <User className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : query && !loading ? (
                <EmptyState
                  icon={Users}
                  title="No users found"
                  description="Try searching with different keywords."
                />
              ) : (
                <EmptyState
                  icon={Users}
                  title="Find other readers"
                  description="Search by name or username to discover new book friends."
                />
              )}
            </TabsContent>

            {/* Suggested Tab */}
            <TabsContent value="suggested" className="space-y-6">
              {recommendationsLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                  <p className="text-muted-foreground mt-4">Finding people you might like...</p>
                </div>
              ) : (
                <>
                  {/* Friends of Friends */}
                  {recommendations?.mutualFollows && recommendations.mutualFollows.length > 0 && (
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-semibold">Friends of Friends</h3>
                        <p className="text-sm text-muted-foreground">People followed by readers you follow</p>
                      </div>
                      <div className="grid gap-4">
                        {recommendations.mutualFollows.map((person: any) => (
                          <Card key={person.id} className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <button onClick={() => navigate(`/u/${person.username}`)}>
                                  <DinoAvatar
                                    src={person.image}
                                    name={person.username || 'User'}
                                    size="md"
                                    className="cursor-pointer"
                                  />
                                </button>
                                <div>
                                  <button
                                    onClick={() => navigate(`/u/${person.username}`)}
                                    className="font-medium hover:underline text-left"
                                  >
                                    {person.name || person.username}
                                  </button>
                                  <p className="text-sm text-muted-foreground">
                                    @{person.username}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {person.mutual_count} mutual {person.mutual_count === 1 ? 'connection' : 'connections'}
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant={person.isFollowing ? "outline" : "default"}
                                onClick={() => handleFollowToggle(person)}
                                disabled={followingLoading[person.id]}
                              >
                                {followingLoading[person.id] ? 'Loading...' : person.isFollowing ? 'Following' : 'Follow'}
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Similar Readers */}
                  {recommendations?.similarReaders && recommendations.similarReaders.length > 0 && (
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-semibold">Similar Readers</h3>
                        <p className="text-sm text-muted-foreground">People who love the same books as you</p>
                      </div>
                      <div className="grid gap-4">
                        {recommendations.similarReaders.map((person: any) => (
                          <Card key={person.id} className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <button onClick={() => navigate(`/u/${person.username}`)}>
                                  <DinoAvatar
                                    src={person.image}
                                    name={person.username || 'User'}
                                    size="md"
                                    className="cursor-pointer"
                                  />
                                </button>
                                <div>
                                  <button
                                    onClick={() => navigate(`/u/${person.username}`)}
                                    className="font-medium hover:underline text-left"
                                  >
                                    {person.name || person.username}
                                  </button>
                                  <p className="text-sm text-muted-foreground">
                                    @{person.username}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {person.shared_books_count} shared {person.shared_books_count === 1 ? 'favorite' : 'favorites'}
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant={person.isFollowing ? "outline" : "default"}
                                onClick={() => handleFollowToggle(person)}
                                disabled={followingLoading[person.id]}
                              >
                                {followingLoading[person.id] ? 'Loading...' : person.isFollowing ? 'Following' : 'Follow'}
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Popular Users */}
                  {recommendations?.trendingUsers && recommendations.trendingUsers.length > 0 && (
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-semibold">Popular Readers</h3>
                        <p className="text-sm text-muted-foreground">Well-followed members of the community</p>
                      </div>
                      <div className="grid gap-4">
                        {recommendations.trendingUsers.map((person: any) => (
                          <Card key={person.id} className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <button onClick={() => navigate(`/u/${person.username}`)}>
                                  <DinoAvatar
                                    src={person.image}
                                    name={person.username || 'User'}
                                    size="md"
                                    className="cursor-pointer"
                                  />
                                </button>
                                <div>
                                  <button
                                    onClick={() => navigate(`/u/${person.username}`)}
                                    className="font-medium hover:underline text-left"
                                  >
                                    {person.name || person.username}
                                  </button>
                                  <p className="text-sm text-muted-foreground">
                                    @{person.username}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {person.follower_count} {person.follower_count === 1 ? 'follower' : 'followers'}
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant={person.isFollowing ? "outline" : "default"}
                                onClick={() => handleFollowToggle(person)}
                                disabled={followingLoading[person.id]}
                              >
                                {followingLoading[person.id] ? 'Loading...' : person.isFollowing ? 'Following' : 'Follow'}
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty state */}
                  {(!recommendations?.mutualFollows?.length && 
                    !recommendations?.similarReaders?.length && 
                    !recommendations?.trendingUsers?.length) && (
                    <div className="text-center py-12">
                      <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        No suggestions available yet. Start following people or rating books to get personalized recommendations!
                      </p>
                    </div>
                  )}
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}